import {
  type DiscountAvailabilityStrategy,
  firstTableDiscountAvailabilityStrategy,
} from "./discountAvailabilityStrategy";
import { type FirebaseAllAvailabilitySlot } from "../../firebase/allAvailability";

function getDiscountAvailabilityDisplayKey(
  slot: Pick<
    FirebaseAllAvailabilitySlot,
    "date" | "session" | "time" | "discount"
  >,
) {
  return [slot.date, slot.session, slot.time, slot.discount].join("|");
}

/**
 * Picks one slot per display key (date, session, time, discount) for the
 * current party size. Colliding diner-band rows are ranked: can seat
 * `people`, then fewer diner options, then lower max diners. Diner field
 * and availability check come from {@link strategy}.
 */
export default function pickPreferredDinerAvailability(
  slots: FirebaseAllAvailabilitySlot[],
  people: number,
  strategy: DiscountAvailabilityStrategy = firstTableDiscountAvailabilityStrategy,
): FirebaseAllAvailabilitySlot[] {
  // Rank inputs are derived once per slot rather than inside the comparator.
  // `isSlotAvailable` parses the slot time via the host, so evaluating it per
  // comparison costs O(n log n) parses for every restaurant and date in the list
  // on every availability snapshot.
  const rankedSlots = slots.map((slot) => {
    const dinersAvailable = strategy.getDinersAvailable(slot);

    return {
      slot,
      unavailable: strategy.isSlotAvailable(slot, people) ? 0 : 1,
      dinerOptionCount: dinersAvailable?.length || Number.MAX_SAFE_INTEGER,
      maxDiners: dinersAvailable?.length
        ? Math.max(...dinersAvailable)
        : Number.MAX_SAFE_INTEGER,
    };
  });

  rankedSlots.sort(
    (slotA, slotB) =>
      slotA.unavailable - slotB.unavailable ||
      slotA.dinerOptionCount - slotB.dinerOptionCount ||
      slotA.maxDiners - slotB.maxDiners,
  );

  const seenSlotKeys = new Set<string>();

  return rankedSlots
    .filter(({ slot }) => {
      const slotKey = getDiscountAvailabilityDisplayKey(slot);
      if (seenSlotKeys.has(slotKey)) {
        return false;
      }

      seenSlotKeys.add(slotKey);
      return true;
    })
    .map(({ slot }) => slot);
}
